import { Box, Grid2, IconButton, Stack, Typography, FormHelperText, List, ListItem, ListItemText } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { confirmSignIn } from 'aws-amplify/auth';
import {
    ButtonComponent,
    CognitoCallbacks,
    CognitoErrorCodes,
    PasswordColorCodeStrength,
    TextFieldComponent,
    theme,
} from '@watter/shared-components';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Formik, Form, ErrorMessage } from 'formik';
import { useTranslation } from 'react-i18next';
import { INavigationConfig, NavigationConfig } from '../../utils/navigation-constants';
import { AppDispatch } from '../../store';
import { LoginUi } from '../login/login-style';
import { PasswordIcon } from '../../components/icons/password-icon';
import { openToast } from '../../store/reducers/toastSlice';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { useHandleLoginOnSuccess } from '../login/login-success';
import { PasswordSetupValidationSchema } from './password-setup-validation-schema';

export const PasswordSetup = () => {
    const navConfig: INavigationConfig = NavigationConfig;
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const type = location.state?.type;
    const username = location.state?.username;
    const { handleLoginOnSuccess } = useHandleLoginOnSuccess();
    const getColorPallet = (password: string) => {
        const colors = {
            red: '#EF4444',
            orange: '#F59E0B',
            green: '#22C55E',
            grey: '#808080',
        };

        const REGEX = {
            password: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*\W).{8,}$/, // Password strength regex
        };

        let colorArray = [colors.grey, colors.grey, colors.grey, colors.grey];
        let strength = 0;

        strength += /[A-Z]+/.test(password) ? 1 : 0; // Uppercase
        strength += /[a-z]+/.test(password) ? 1 : 0; // Lowercase
        strength += /\d+/.test(password) ? 1 : 0; // Numbers
        strength += /[!@#$%^&*()_+\-={}';:"\\|,.<>?]+/.test(password) ? 1 : 0; // Special characters

        if (password.length < 8 && strength > 2) {
            strength -= 1;
        }

        if (password.length >= 8 && REGEX.password.test(password)) {
            strength = 4;
        }

        switch (strength) {
            case PasswordColorCodeStrength.LOWER_STRENGTH:
                colorArray = [colors.red, colors.grey, colors.grey, colors.grey];
                break;
            case PasswordColorCodeStrength.MINIMUM_STRENGTH:
                colorArray = [colors.orange, colors.orange, colors.grey, colors.grey];
                break;
            case PasswordColorCodeStrength.MEDIUM_STRENGTH:
                colorArray = [colors.green, colors.green, colors.green, colors.grey];
                break;
            case PasswordColorCodeStrength.HIGH_STRENGTH:
                colorArray = [colors.green, colors.green, colors.green, colors.green];
                break;
            default:
                break;
        }

        return colorArray;
    };

    // Handle form submission
    const handleSetPassword = async (values: { password: string; confirmPassword: string }) => {
        try {
            dispatch(showLoader());
            const res: any = await confirmSignIn({
                challengeResponse: values?.password,
            });
            switch (res?.nextStep?.signInStep) {
                case CognitoCallbacks.CONFIRM_SIGN_IN_WITH_SMS_CODE:
                case CognitoCallbacks.CONFIRM_SIGN_IN_WITH_EMAIL_CODE:
                    navigate(navConfig.LoginOtpScreen, {
                        state: {
                            username,
                            password: values.password,
                            isCalledFrom: NavigationConfig.Login,
                            maskedNumber: res?.nextStep?.codeDeliveryDetails?.destination,
                            type: CognitoCallbacks.CONFIRM_SIGN_IN_WITH_SMS_CODE
                                ? CognitoCallbacks.CONFIRM_SIGN_IN_WITH_SMS_CODE
                                : CognitoCallbacks.CONFIRM_SIGN_IN_WITH_EMAIL_CODE,
                        },
                    });
                    break;
                case CognitoCallbacks.DONE:
                    await handleLoginOnSuccess();
                    break;
                default:
            }
        } catch (err: any) {
            switch (err?.name) {
                case CognitoErrorCodes.NOT_AUTHORIZED_EXCEPTION:
                    dispatch(
                        openToast({
                            show: true,
                            message: t('otp_expired_message'),
                            severity: 'error',
                            isAlertOpen: true,
                        }),
                    );
                    break;
                case CognitoErrorCodes.SIGN_IN_EXCEPTION:
                    dispatch(
                        openToast({
                            show: true,
                            message: t('sign_in_exception_message'),
                            severity: 'error',
                            isAlertOpen: true,
                        }),
                    );
                    break;
                default:
                    dispatch(openToast({ show: true, message: err?.name, severity: 'error', isAlertOpen: true }));
            }
        } finally {
            dispatch(hideLoader());
        }
    };

    // handle next
    const handleResetPassword = (values: { password: string; confirmPassword: string }) => {
        navigate(navConfig.LoginOtpScreen, {
            state: {
                username,
                password: values.password,
                confirmPassword: values.confirmPassword,
                isCalledFrom: NavigationConfig.ForgotPassword,
                type: CognitoCallbacks.CONFIRM_RESET_PASSWORD_WITH_CODE,
            },
        });
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <LoginUi container spacing={{ sm: 0, lg: 2 }}>
            <Grid2 className="imgContainer" size={{ sm: 12, lg: 8 }} display="flex" alignItems="center">
                <Box />
            </Grid2>
            <Grid2 size={{ sm: 12, lg: 4 }} className="feildsLabels" position="relative">
                <IconButton className="backBtn" onClick={handleBack}>
                    <KeyboardBackspaceIcon />
                </IconButton>
                <Box height="100%">
                    <Box
                        pt={3}
                        px={5}
                        pb={2}
                        height="100%"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                    >
                        <Box textAlign="center" className="logo passwordLogo ">
                            <PasswordIcon />
                        </Box>
                        <Typography variant="h4" pt={1.5} textAlign="center">
                            {t('password_setup_title')}
                        </Typography>
                        <Formik
                            initialValues={{ password: '', confirmPassword: '' }}
                            validationSchema={PasswordSetupValidationSchema(t)}
                            onSubmit={values => {
                                if (type === CognitoCallbacks.CONFIRM_RESET_PASSWORD_WITH_CODE) {
                                    handleResetPassword(values);
                                } else {
                                    handleSetPassword(values);
                                }
                            }}
                        >
                            {({ values, setFieldValue }: any) => (
                                <Form>
                                    <Stack mb={1.25} className="cstInput">
                                        <TextFieldComponent
                                            name="password"
                                            type="password"
                                            placeholder={t('new_password_placeholder')}
                                            value={values.password}
                                            onChange={(e: any) => setFieldValue('password', e.target.value.trim())}
                                            maxLength={20}
                                        />

                                        <FormHelperText error>
                                            <ErrorMessage name="password" />
                                        </FormHelperText>
                                    </Stack>
                                    <Stack mb={1.25} className="cstInput">
                                        <TextFieldComponent
                                            name="confirmPassword"
                                            type="password"
                                            placeholder={t('confirm_password_placeholder')}
                                            value={values.confirmPassword}
                                            onChange={(e: any) =>
                                                setFieldValue('confirmPassword', e.target.value.trim())
                                            }
                                            maxLength={20}
                                        />
                                        <FormHelperText error>
                                            <ErrorMessage name="confirmPassword" />
                                        </FormHelperText>
                                    </Stack>
                                    <Stack direction="row" spacing={1.5} mb={2.5}>
                                        {getColorPallet(values.password).map((color, idx) => {
                                            const barKey = `password-strength-bar-${idx}-${color}`;
                                            return (
                                                <Box
                                                    key={barKey}
                                                    height={theme.spacing(0.5)}
                                                    width="25%"
                                                    bgcolor={color}
                                                    borderRadius={theme.spacing(1.25)}
                                                />
                                            );
                                        })}
                                    </Stack>
                                    <Stack pb={1.5}>
                                        <Typography variant="subtitle1" lineHeight="1.35" color="info">
                                            {t('new_password_required_text')}
                                        </Typography>
                                        <Typography
                                            variant="subtitle1"
                                            lineHeight="1.35"
                                            color="info"
                                            sx={{ height: '10px' }}
                                        >
                                            {t('new_password_include_text1')}
                                        </Typography>
                                        <List dense>
                                            {[
                                                t('new_password_include_text2'),
                                                t('new_password_include_text3'),
                                                t('new_password_include_text4'),
                                                t('new_password_include_text5'),
                                            ].map(text => (
                                                <ListItem key={text} sx={{ py: 0 }}>
                                                    <ListItemText
                                                        primary={
                                                            <Typography
                                                                variant="subtitle1"
                                                                lineHeight="1.35"
                                                                color="info"
                                                            >
                                                                • {text}{' '}
                                                            </Typography>
                                                        }
                                                        sx={{ m: 0 }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Stack>
                                    <Box className="cstBtnWrapper">
                                        <ButtonComponent
                                            size="medium"
                                            variant="contained"
                                            fullWidth
                                            buttonName={
                                                type === CognitoCallbacks.CONFIRM_RESET_PASSWORD_WITH_CODE
                                                    ? t('next')
                                                    : t('submit')
                                            }
                                            type="submit"
                                            className="cstBtn"
                                        />
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                </Box>
            </Grid2>
        </LoginUi>
    );
};

export default PasswordSetup;
